import { action, makeAutoObservable } from 'mobx'
import { observer } from 'mobx-react'
import { PropsWithChildren, createContext, useContext } from 'react'
import {
	BaseWorkflowTaskState,
	WriteWorkflowTaskModel,
} from '../../../api/clients/workflowTasks/DTOs'
import { InteractiveWorkflowTaskResult } from './IInteractiveWorkflowTask'
import { getGlobalInteractiveWorkflowTaskRegistry } from './InteractiveWorkflowTaskRegistry'

export type TaskProps = {
	close(result: InteractiveWorkflowTaskResult): void
}

type InteractiveWorkflowTaskTracker<
	T extends WriteWorkflowTaskModel<BaseWorkflowTaskState> = WriteWorkflowTaskModel<BaseWorkflowTaskState>,
> = {
	task: T
	resolvePromise(result: InteractiveWorkflowTaskResult): void
}

class InteractiveWorkflowTaskService {
	public taskTrackers: InteractiveWorkflowTaskTracker[] = []

	constructor() {
		makeAutoObservable(this)
	}

	public displayTask<T extends WriteWorkflowTaskModel<BaseWorkflowTaskState>>(
		task: T,
	): Promise<InteractiveWorkflowTaskResult> | undefined {
		// if we already have a component to display for this task we don't want to add twice
		if (this.taskTrackers.map((v) => v.task.id).includes(task.id))
			return undefined

		// @ts-expect-error - we have to have this assignment here, JS spec says ctor of promise is sync so we can export
		let promiseResolve: (value: InteractiveWorkflowTaskResult) => void =
			undefined

		const promise = new Promise<InteractiveWorkflowTaskResult>((resolve) => {
			promiseResolve = resolve
		})

		this.taskTrackers.push({
			task: task,
			resolvePromise: promiseResolve,
		})

		promise.then(
			action(() => {
				this.taskTrackers.shift()
			}),
		)

		return promise
	}
}

const interactiveWorkflowTaskService = new InteractiveWorkflowTaskService()

const context = createContext(interactiveWorkflowTaskService)

export const InteractiveWorkflowTaskContextProvider = (
	props: PropsWithChildren<unknown>,
) => {
	return (
		<context.Provider value={interactiveWorkflowTaskService}>
			<TaskDisplayComponent>{props.children}</TaskDisplayComponent>
		</context.Provider>
	)
}

export type InteractiveWorkflowTaskServiceType = {
	displayTask<T extends WriteWorkflowTaskModel<BaseWorkflowTaskState>>(
		task: T,
	): Promise<InteractiveWorkflowTaskResult> | undefined
}

export const useInteractiveTasks = (): InteractiveWorkflowTaskServiceType => {
	const contextInstance = useContext(context)
	return contextInstance
}

const TaskDisplayComponent = (props: PropsWithChildren<unknown>) => {
	return (
		<>
			<TaskDisplay />
			{props.children}
		</>
	)
}

const TaskDisplay = observer(() => {
	const length = interactiveWorkflowTaskService.taskTrackers.length

	if (length === 0) return <></>

	const firstTracker = interactiveWorkflowTaskService.taskTrackers[0]

	const getTaskDefinition = (definitionId: string) => {
		const registry = getGlobalInteractiveWorkflowTaskRegistry()
		return registry.workflowTaskCollection[definitionId]
	}

	const Interaction = getTaskDefinition(
		firstTracker.task.workflowTaskState.workflowTaskTypeId,
	).interaction

	return (
		<Interaction
			task={firstTracker.task}
			onCancel={(result) => firstTracker.resolvePromise(result)}
			onConfirm={(result) => firstTracker.resolvePromise(result)}
		/>
	)
})
